'use strict';

import React, {useState, useRef, useEffect} from "react";
import PropTypes from 'prop-types';
import classnames from "classnames";
import focusTrap from "focus-trap-js";

import {QuestionCircleFill} from "react-bootstrap-icons";

import Button from 'react-bootstrap/Button';
import Popover from 'react-bootstrap/Popover';
import Overlay from 'react-bootstrap/Overlay';

import {makeUniqueId} from './Utils'

/**
 * Popover for help content.
 */
const HelpPopover = ({children, title = "Help", icon = QuestionCircleFill, link, size, flip, rotate, placement = 'right', className, variant = 'help'}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [target, setTarget] = useState(null);
    const ref = useRef(null);

    const closeTooltip = () => {
        setShowTooltip(false);
    };

    const openTooltip = (event) => {
        setShowTooltip(true);
        setTarget(event.target);
    };

    const handleKeyDown = (event) => {
        if ([13, 32].includes(event.which)) {
            event.preventDefault();
            openTooltip(event);
        }
    };

    const handleClose = (event) => {
        if ([13, 32].includes(event.which)) {
            event.preventDefault();
            closeTooltip(event);
        }
    }

    const popoverId = title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-').toLowerCase() + "-" + makeUniqueId();

    const popoverOverlay =
        <Popover id={popoverId + "-popover"} className="popover-help axdd-font-open-sans shadow-sm">
            <Popover.Header className="fw-bold position-relative">
                {title}
                <button id={popoverId + "-close"}
                        type="button"
                        className="fs-8 btn-close position-absolute top-50 end-0 translate-middle-y me-2"
                        onClick={closeTooltip}
                        onKeyDown={handleClose}
                        aria-label="Close Popover"></button>
            </Popover.Header>
            <Popover.Body>
                {children}
            </Popover.Body>
        </Popover>;

    let actionItem;

    if (link && link !== "") {
        actionItem = link;
    } else {
        let iconAttr = {};
        let iconTransform = "";
        if (flip) {
            switch(flip) {
                case "horizontal":
                    iconTransform += " scaleY(-1)";
                    break;
                case "vertical":
                    iconTransform += " scaleX(-1)";
                    break;
                case "both":
                    iconTransform += " scale(-1)";
                    break;
            }
        }
        if (rotate) {
            iconTransform += ` rotate(${rotate}deg)`;
        }

        if (iconTransform !== "") {
            iconAttr["style"] = {transform: iconTransform};
        }

        if (size) {
            iconAttr["size"] = size;
        }

        const Icon = icon;

        actionItem = <Icon aria-hidden={true} className="align-top" {...iconAttr} />
    }

    return (
        <div className="d-inline lh-1" ref={ref}>
            <Button id={popoverId + "-trigger"}
                    onClick={openTooltip}
                    onKeyDown={handleKeyDown}
                    variant={variant}
                    className={classnames(className, "lh-1")}
                    title={title}
                    aria-label={title}
                    aria-haspopup="true"
                    tabIndex={0}>
                {actionItem}
            </Button>
            <Overlay show={showTooltip}
                     target={target}
                     placement={placement}
                     container={ref.current}
                     onHide={closeTooltip}
                     rootClose>
                {popoverOverlay}
            </Overlay>
        </div>
    );
}

HelpPopover.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func
    ]),
    link: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    size: PropTypes.string,
    flip: PropTypes.string,
    rotate: PropTypes.number,
    placement: PropTypes.string,
    className: PropTypes.string,
    variant: PropTypes.string,
};

export default HelpPopover;
